import { Select, Spin, Tag } from 'antd';
import debounce from 'lodash/debounce';
import React, { useMemo, useState } from 'react';
import orgService from 'services/orgService';
import { useOrgId } from 'hooks/useOrg';
import { UserContactAddress } from 'domains/orgDomain';
import { DEBOUNCE_TIME } from 'utils/constants';
import { useMount } from 'ahooks';
import { CustomTagProps } from 'rc-select/lib/interface/generator';
import ContactLink from 'features/Workspace/components/ContactLink';

export interface SelectValue {
  label: string;
  value: number;
}

export interface CsrSelectProps {
  initValue?: UserContactAddress[];
  onChange: (params?: UserContactAddress[]) => void;
}

export const MAX_TAG_COUNT = 2;

function CsrSelect({ initValue, onChange }: CsrSelectProps) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<SelectValue[]>([]);
  const [currentOptions, setCurrentOptions] = useState<UserContactAddress[]>(
    []
  );

  const orgId = useOrgId();
  const [value, setValue] = useState<SelectValue[]>([]);

  const formatOptionLabel = (item: UserContactAddress): SelectValue => {
    const { fullName, contactId } = item;
    return {
      label: fullName,
      value: contactId,
    };
  };
  useMount(() => {
    if (initValue) {
      const v = initValue.map(formatOptionLabel);
      setValue(v);
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function getCsrList(key: string): Promise<SelectValue[]> {
    const res = await orgService.getOrgCsrContactList(orgId, key);
    setCurrentOptions(res);
    return Promise.resolve(res.map(formatOptionLabel));
  }

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      setOptions([]);
      setFetching(true);
      getCsrList(value).then((newOptions) => {
        setOptions(newOptions);
        setFetching(false);
      });
    };
    return debounce(loadOptions, DEBOUNCE_TIME);
  }, [getCsrList]);

  const getResourceBySelectValue = (selectValue: SelectValue[]) => {
    return selectValue.map((item) => {
      const { value } = item;
      return [...currentOptions, ...(initValue || [])].find(
        (item) => item.contactId === value
      );
    }) as UserContactAddress[];
  };

  const tagRender = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const u = getResourceBySelectValue([{ label, value }] as SelectValue[])[0];
    if (u) {
      return (
        <div>
          <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
            <ContactLink userContact={u} />
          </Tag>
        </div>
      );
    }
    return <></>;
  };

  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      options={options}
      placeholder="Type the user names here"
      mode="multiple"
      value={value}
      tagRender={tagRender}
      autoClearSearchValue={true}
      onChange={(newValue) => {
        if (newValue.length <= MAX_TAG_COUNT) {
          const v = newValue as SelectValue[];
          setValue(v);
          onChange(getResourceBySelectValue(v));
          setOptions([]);
        }
      }}
    />
  );
}

export default CsrSelect;
